import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

const config = {
    success: {
        icon: <CheckCircle className="w-5 h-5 fill-emerald-500 text-white" />,
        classes: "bg-white border-slate-100 text-slate-900 shadow-slate-200/50",
        bar: "bg-emerald-500"
    },
    error: {
        icon: <XCircle className="w-5 h-5 fill-rose-500 text-white" />,
        classes: "bg-white border-slate-100 text-slate-900 shadow-slate-200/50",
        bar: "bg-rose-500"
    },
    warning: {
        icon: <AlertCircle className="w-5 h-5 fill-amber-500 text-white" />,
        classes: "bg-white border-slate-100 text-slate-900 shadow-slate-200/50",
        bar: "bg-amber-500"
    },
    info: {
        icon: <Info className="w-5 h-5 fill-blue-500 text-white" />,
        classes: "bg-white border-slate-100 text-slate-900 shadow-slate-200/50",
        bar: "bg-blue-500"
    },
};

const Alerta = ({ tipo = "info", titulo = "", mensaje = "", onClose = null, duration = 5000, onClick = null }) => {
    useEffect(() => {
        if (duration && onClose) {
            const timer = setTimeout(onClose, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    const { icon, classes, bar } = config[tipo] || config.info;

    const getTituloDefecto = () => {
        if (titulo) return titulo;
        switch (tipo) {
            case "success": return "Éxito";
            case "error": return "Error";
            case "warning": return "Advertencia";
            case "info":
            default:
                return "Información";
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            className="fixed top-6 left-4 right-4 md:left-auto md:right-6 md:w-auto z-[9999] pointer-events-none"
        >
            <div 
                onClick={onClick}
                className={twMerge(
                    "pointer-events-auto relative p-6 rounded-[1.5rem] border border-slate-150 shadow-xl w-full md:min-w-[320px] md:max-w-sm flex flex-col gap-3",
                    onClick && "cursor-pointer hover:shadow-2xl hover:translate-y-[-2px] active:translate-y-0 transition-all",
                    classes
                )}
            >
                {/* Progress Bar Animation */}
                {duration && (
                    <motion.div
                        initial={{ width: "100%" }}
                        animate={{ width: "0%" }}
                        transition={{ duration: duration / 1000, ease: "linear" }}
                        className={twMerge("absolute bottom-0 left-0 h-1 opacity-20", bar)}
                    />
                )}

                {/* Top Section: Icon, Title, Close Button */}
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="shrink-0">
                            {icon}
                        </div>
                        <h4 className="text-sm font-black text-slate-800 tracking-tight leading-none">
                            {getTituloDefecto()}
                        </h4>
                    </div>
                    {onClose && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onClose(); }}
                            className="shrink-0 p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Message Section */}
                <div className="pl-7.5">
                    <p className="text-xs text-slate-500 font-semibold leading-relaxed">
                        {mensaje}
                    </p>
                </div>

                {/* Action Button Section */}
                {onClick && (
                    <div className="flex justify-end mt-1">
                        <button
                            onClick={(e) => { e.stopPropagation(); onClick(); }}
                            className="px-4 py-2 bg-primary text-white text-[10px] font-black uppercase tracking-wider rounded-xl hover:bg-primary/95 transition-all shadow-md active:scale-95"
                        >
                            Ver indicaciones
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default Alerta;
